import React, { useEffect, useState } from 'react';
import { CardSection } from '../../shared/components/card/CardSection';
import { IconButton, InputBase, Paper, Box, Stack, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { TableDefault } from '../../shared/components/table/TableDefault';
import { TableColumnType } from '../../shared/components/table/table-types';
import moment from 'moment';
import { DateRangePickerWithText } from '../../shared/components/DateRangePicker';
import { formatterDateFilter } from '../../shared/utils';
import { DateFilter } from '../../shared/constants';
import { useBreakPoint } from '../../shared/hooks/useBreakPoint';
import { useNavigate } from 'react-router-dom';
import {
  SortOrder,
  UserOrderInput,
  useFetchUserSubscriberEventsLazyQuery,
  UserSubscriberEvent,
} from '../../shared/types/generated';
import { openSnackbarAction } from '../../shared/components/snackbar/snackbar-actions';
import { TypeMessage } from '../../shared/components/snackbar/snackbar-types';

export const SubscriptionUsersEvents: React.FC = () => {
  const navigate = useNavigate();
  const [total, setTotal] = useState<number>(0);
  const { isBelow } = useBreakPoint('sm');
  const [{ from, to }, setDate] = useState<{
    from: string | undefined;
    to: string | undefined;
  }>({
    from: undefined,
    to: undefined,
  });
  const [{ page, pageSize }, setPagination] = useState({
    pageSize: 10,
    page: 1,
  });

  const [search, setSearch] = useState('');

  const [callUserSubscribers, { data, loading, refetch }] = useFetchUserSubscriberEventsLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: (d) => {
      //console.log(d);
      setTotal(d?.userSubscriberEvents?.count || 0);
    },
    onError: (error) => {
      console.error(error);
      openSnackbarAction(
        'Ha ocurrido un error al intentar obtener los usuarios suscritos',
        TypeMessage.ERROR,
      );
    },
  });

  const handleUserDetailsClick = (item: UserSubscriberEvent): void => {
    navigate(`/users/${item?.user?.id}/subscriptions`);
  };

  useEffect(() => {
    callUserSubscribers({
      variables: {
        where: {
          search,
          created_at: {
            lte: to,
            gte: from,
          },
        },
        skip: (page - 1) * pageSize,
        take: pageSize,
      },
    });
  }, [page, pageSize, callUserSubscribers, from, to, search]);

  const COLUMNS: TableColumnType<UserSubscriberEvent>[] = [
    {
      columnName: 'NOMBRE',
      columnValue: (item) => item?.user?.name || '',
    },
    {
      columnName: 'EMAIL',
      columnValue: null,
      columnComponent: (item) => <Box>{item?.user?.email || ''}</Box>,
    },
    {
      columnName: 'FECHA',
      columnValue: (item) =>
        item?.created_at
          ? moment(parseInt(item?.created_at)).utc(false).format('YYYY-MM-DD HH:mm')
          : '',
    },
    {
      columnName: 'EVENTO',
      columnValue: (item) => item?.event?.description || '',
    },
  ];

  const users = (data?.userSubscriberEvents?.items || []) as UserSubscriberEvent[];
  //console.log({ from }, { to });
  return (
    <Box sx={{ width: '100%', background: 'white' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        width={'100%'}
        alignItems={'center'}
        justifyContent={{ xs: 'space-between' }}
        py={1}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box pl={2} display={'flex'} alignItems={'center'}>
          <Typography fontSize={{ sx: '16px', md: '20px' }} fontWeight={600}>
            Eventos de suscriptores
          </Typography>
        </Box>

        <Box sx={{ width: { sx: '100%', md: 'auto' } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            padding={'10px 0px'}
            spacing={1}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Paper
              sx={{
                p: '2px 4px',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                maxWidth: 400,
                height: '30px',
              }}
            >
              <InputBase
                sx={{ ml: 1, flex: 1 }}
                placeholder={isBelow ? 'BUSCAR' : 'BUSCAR USUARIO'}
                inputProps={{ 'aria-label': 'search google maps' }}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                size="small"
                onKeyUp={(e) => {
                  if (e.code === 'Enter') {
                    setPagination({
                      pageSize,
                      page: 1,
                    });
                  }
                }}
              />

              <IconButton
                type="button"
                sx={{ p: '10px' }}
                aria-label="search"
                onClick={() => {
                  setPagination({
                    pageSize,
                    page: 1,
                  });
                }}
              >
                <SearchIcon />
              </IconButton>
            </Paper>
            <Box display="flex" alignItems="center">
              <DateRangePickerWithText
                onChange={(range) => {
                  const filters = formatterDateFilter(
                    range.startDate,
                    range.endDate,
                    range.key as DateFilter,
                  );

                  setDate({
                    from: filters.startDate,
                    to: filters.endDate,
                  });
                  //console.log(filters.startDate, filters.endDate);
                }}
              />
            </Box>
          </Stack>
        </Box>
      </Stack>
      <CardSection title={''}>
        <TableDefault
          items={users}
          count={total}
          columns={COLUMNS}
          page={page}
          pageSize={pageSize}
          loading={loading}
          messageLoading={'Cargando usuarios...'}
          messageEmpty="No se encontraron eventos de suscriptores"
          //handleClickRow={(item) => handleUserDetailsClick(item)}
          handleChangePage={(p) => {
            setPagination({
              pageSize,
              page: p + 1,
            });
          }}
          showPagination
          handleClickRow={(item) => handleUserDetailsClick(item)}
          rowsPerPageOptions={[10, 20, 50, 100]}
          handleChangeRowPerPage={(e) => {
            setPagination({
              pageSize: parseInt(e.target.value, 10),
              page: 1,
            });
          }}
        />
      </CardSection>
    </Box>
  );
};
